import {
    GOOGLE_ONE_TAP_PARENT_ID,
    GOOGLE_ONE_TAP_TIMEOUT_SUPPRESSION
} from './constants';
import type {
    TGoogleOneTapReasons,
    TGoogleOneTapStatesHandlers,
    PromptMomentNotification,
    TGoogleOneTapStates
} from './googleOneTap.types';
import { trackErrors, trackSuccess } from './googleOneTapAnalytics';
import { handleOneTapResponse, shouldLoadOneTap } from './utils';
import { trackError } from '../utils/error.utils';

let isOneTapCanceled = false;

const googleOneTapOptions = {
    auto_select: false,
    callback: result => {
        trackSuccess('one_tap_credential_returned');
        handleOneTapResponse(result);
    },
    cancel_on_tap_outside: false,
    client_id: process.env.GATSBY_google_api_client_id!,
    context: 'marketing_site',
    prompt_parent_id: GOOGLE_ONE_TAP_PARENT_ID,
    prompt_suppress_sec: GOOGLE_ONE_TAP_TIMEOUT_SUPPRESSION,
    state_cookie_domain: 'honeybook.com',
    use_fedcm_for_prompt: true
};

const handleError = (reason: TGoogleOneTapReasons) => {
    // Don't track errors for empty or undefined reasons - this is normal user behavior
    if (!reason || reason.trim() === '') {
        cancelOneTap();
        return;
    }

    trackErrors(reason);
    trackError(`Google One Tap error: ${reason}`, {
        reason,
        source: 'google_one_tap',
        platform: 'gatsby'
    });
    cancelOneTap();
};

const handleOneTapMoments: TGoogleOneTapStatesHandlers = {
    skipped(_notification: PromptMomentNotification) {},
    dismissed(notification: PromptMomentNotification) {
        const reason = notification.getDismissedReason();
        // credential_returned is the reason when google return as the user credentials successfully even thought its inside dismissed state
        if (reason !== 'credential_returned') {
            handleError(reason);
        }
    }
};

const handleOnLoad = () => {
    if (isOneTapCanceled) {
        return;
    }
    const onNotification = (notification: PromptMomentNotification) => {
        const momentType: TGoogleOneTapStates = notification.getMomentType();
        if (handleOneTapMoments[momentType]) {
            handleOneTapMoments[momentType](notification);
        }
    };

    try {
        trackSuccess('load');
        trackSuccess('display');
        window.google?.accounts.id.initialize(googleOneTapOptions);
        window.google?.accounts.id.prompt(onNotification);
    } catch (error) {
        trackError('Google One Tap initialization failed', {
            source: 'google_one_tap',
            platform: 'gatsby',
            error_type: 'initialization_failure',
            error_message:
                error instanceof Error ? error.message : String(error)
        });
        handleError('load_error');
    }
};

const cancelOneTap = () => window.google?.accounts?.id?.cancel();

export const initGoogleOneTap = async () => {
    let _shouldLoadOneTap = await shouldLoadOneTap();
    if (!_shouldLoadOneTap) {
        return;
    }
    const googleOneTapScript = document.createElement('script');
    googleOneTapScript.setAttribute('type', 'text/javascript');
    googleOneTapScript.setAttribute(
        'src',
        'https://accounts.google.com/gsi/client'
    );
    googleOneTapScript.onload = handleOnLoad;
    googleOneTapScript.onerror = () => {
        trackError('Google One Tap script failed to load', {
            source: 'google_one_tap',
            platform: 'gatsby',
            error_type: 'script_load_failure'
        });
        handleError('load_error');
    };
    document.head.appendChild(googleOneTapScript);
};
